import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Text,
  Content,
  FlexboxGrid,
  Form,
  Row,
  Col,
  IconButton,
  Grid,
} from 'rsuite';
import { updatePermitApplicationData } from '../../store/slices/apply/applySlice';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import FormTextField from '../../components/form/FormTextField';
import PlusIcon from '@rsuite/icons/Plus';
import CloseIcon from '@rsuite/icons/Close';
import { urlValidation } from '../../common/validation';
import { FLOWS_ENUM } from '../../common/enums';
import { selectApplyApplicationData } from '../../store/slices/config/configSelectors';

const ApplyListings = () => {
  const dispatch = useDispatch();
  const form = useRef();
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState({});

  const [websites, setWebsites] = useState([
    { url: '', idx: 0, key: `website_url_0`, isValid: false },
  ]);
  const [lastWebsiteIndex, setLastWebsiteIndex] = useState(0);

  const { listings } = useSelector(selectApplyApplicationData);

  const formModel = useMemo(() => {
    let model = {};
    websites.forEach((w) => {
      model[w.key] = w.url;
    });
    return model;
  }, [websites]);

  const validationModel = useMemo(() => {
    let _validation = {};
    Object.keys(formModel).forEach((k) => {
      _validation[k] = urlValidation;
    });
    return _validation;
  }, [formModel]);

  useEffect(() => {
    if (listings?.length > 0) {
      let existingWebsites = listings.map((listingUrl, idx) => {
        return {
          url: listingUrl,
          idx: idx,
          key: `website_url_${idx}`,
          isValid: false,
        };
      });

      let existingFormValues = {};
      existingWebsites.forEach((ew) => {
        existingFormValues[ew.key] = ew.url;
      });

      setLastWebsiteIndex(existingWebsites.length);
      setWebsites(existingWebsites);
      setFormValues(existingFormValues);
    }
  }, [listings]);

  const onAddListingClicked = () => {
    let tempWebsites = [...websites];
    let indexValueToAdd = lastWebsiteIndex + 1;
    let newWebsiteRow = {
      url: '',
      idx: indexValueToAdd,
      key: `website_url_${indexValueToAdd}`,
      isValid: false,
    };
    tempWebsites.push(newWebsiteRow);

    setLastWebsiteIndex(indexValueToAdd);
    setWebsites(tempWebsites);
    setFormValues((prev) => ({
      ...prev,
      [newWebsiteRow.key]: newWebsiteRow.url,
    }));
  };

  const onRemoveWebsiteClicked = (key) => {
    let tempWebsites = [...websites];
    tempWebsites = tempWebsites.filter((website) => website.key !== key);
    setWebsites(tempWebsites);
    let newFormValues = { ...formValues };
    delete newFormValues[key];
    setFormValues(newFormValues);
  };

  const dispatchListings = () => {
    if (!form?.current?.check()) return false;
    let websiteArr = Object.keys(formValues).map((k) => formValues[k]);
    dispatch(updatePermitApplicationData({ listings: websiteArr }));
    return true;
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title="Listings"
            onNext={dispatchListings}
          >
            <Form
              ref={form}
              checkTrigger="none"
              formValue={formValues}
              onCheck={setFormError}
              onChange={setFormValues}
              fluid
            >
              <FlexboxGrid
                role="grid"
                className="mt-12 ml-0"
                xs={24}
                lg={12}
              >
                <FlexboxGrid.Item
                  as={Row}
                  colspan={24}
                >
                  <Col>
                    <Text>
                      Please report each listing advertising your short-term
                      rental.
                    </Text>
                    <Text className="text-bold">This is optional.</Text>
                  </Col>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  as={Row}
                  colspan={24}
                  className="mt-24"
                >
                  <div role="cell">
                    <Grid
                      role="grid"
                      fluid
                    >
                      <Row style={{ minHeight: '60px' }}>
                        <Col xs={24}>
                          {websites?.map((website, idx) => {
                            return (
                              <FlexboxGrid
                                key={`website_url_container_${idx}`}
                                justify="start"
                                align="middle"
                              >
                                <FlexboxGrid.Item colspan={20}>
                                  <FormTextField
                                    key={website.key}
                                    name={website.key}
                                    value={formValues[website.key]}
                                    hasError={formError[website.key]}
                                    errorMessage={`Please enter a valid URL address`}
                                    validationRule={
                                      validationModel[website.key]
                                    }
                                    bottomMarginOverrideClass="override-form-field-bottom-margin-8"
                                    inputFieldType="url"
                                    inputMode="url"
                                    preventHtmlValidation={true}
                                  />
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item className="pl-12">
                                  <IconButton
                                    aria-label="Delete url input"
                                    className="override-form-field-bottom-margin-8"
                                    icon={<CloseIcon />}
                                    onClick={() =>
                                      onRemoveWebsiteClicked(website.key)
                                    }
                                  />
                                </FlexboxGrid.Item>
                              </FlexboxGrid>
                            );
                          })}
                        </Col>
                      </Row>
                    </Grid>
                  </div>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  as={Row}
                  colspan={24}
                  className="mt-16"
                >
                  <Col xs={12}>
                    <IconButton
                      icon={<PlusIcon />}
                      onClick={onAddListingClicked}
                    >
                      Add listing
                    </IconButton>
                  </Col>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Form>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ApplyListings;
